import React from "react";
import { useDispatch } from "react-redux";
import { addCartItem, addNewCart, deleteCart, fetchCartByCustomerId, removeCartItem, updateCartItem } from "../redux/cartSlice";

interface CartItem {
    cartItemId?: number;
    product: {
        productId: number;
        productName: string;
        salePrice: number;
        mrpPrice?: number;
        taxPercent?: number;
    };
    quantity: number;
    unitPrice: number;
    totalPrice: number;
}

interface Cart {
    cartId: number;
    cartName: string;
    cartItems: CartItem[];
    customerId: number;
}

export const addToCart = (
    cartName: string,
    itemProduct: CartItem["product"],
    quantity: number,
    comboList: CartItem[] = [],
    installationService: CartItem | null = null,
    dispatch: any
) => {
    const custId = localStorage.getItem("customerId");
    if (!custId) {
        console.error("User not logged in");
        return false;
    }
    let customerCart: any = getCustomerCart(dispatch) || [];
    const cartData: any = getCartByCartName(cartName) || {};
    const cartItems = cartData && cartData?.cartItems && cartData?.cartItems.length > 0 ? [...cartData.cartItems] : [];

    //console.log(itemProduct, comboList, installationService);
    if (itemProduct) {
        let itemPrice = (itemProduct.mrpPrice! + (itemProduct.mrpPrice! * itemProduct.taxPercent!) / 100);
        let cartItem: any = {
            product: {
                productId: itemProduct.productId,
                productName: itemProduct.productName,
                salePrice: itemProduct.salePrice ? itemProduct.salePrice : 0,
            },
            quantity: quantity,
            unitPrice: itemProduct.mrpPrice ? itemProduct.mrpPrice : 0,
            totalPrice: itemPrice * quantity,
        };

        let newCartItems: any[] = [cartItem];

        if (comboList && comboList.length > 0) {
            newCartItems = [...newCartItems, ...comboList];
        }

        if (installationService) {
            newCartItems = [...newCartItems, installationService];
        }

        let isExistingItem: boolean = false;
        let existingItem: any = null;

        if (cartItems && cartItems.length > 0) {
            newCartItems.forEach((item: any) => {
                let updatedItems: any[] = [...cartItems];
                if (cartItems && cartItems.length > 0) {
                    existingItem = cartItems.find((x: any) => x.product.productId == item.product.productId);
                }

                if (existingItem) {
                    isExistingItem = true;
                    let newQuantity = existingItem.quantity + quantity;
                    let itemPrice = (itemProduct.mrpPrice! + (itemProduct.mrpPrice! * itemProduct.taxPercent!) / 100);
                    existingItem = {
                        ...existingItem,
                        "quantity": newQuantity,
                        "totalPrice": itemPrice * newQuantity
                    }
                    let index = updatedItems.findIndex((x) => {
                        return x.product.productId === existingItem.product.productId;
                    });
                    updatedItems[index] = { ...existingItem };
                    let item: any = { cartId: cartData.cartId, ...existingItem };
                    updateItemToCart(item, dispatch);
                } else {
                    updatedItems.push(item);
                    item['product']['salePrice'] = item.unitPrice;
                    item = { cartId: cartData.cartId, ...item };
                    addItemToCart(item, dispatch);
                }
            })
        } else {
            const addcart: any = {
                cartId: 0,
                cartName: cartName,
                cartItems: newCartItems,
                customerId: custId,
            };
            addNewCustomerCart(addcart, dispatch);
        }

        return true;
    }
};

export const removeItem = async (cartName: string, index: number, dispatch: any): Promise<void> => {
    const cartData = getCartByCartName(cartName);
    if (!cartData) return;

    const cartItems = [...cartData!.cartItems];
    if (index < 0 || index >= cartItems.length) {
        console.error("Invalid index");
        return;
    }

    const removedItem = cartItems.splice(index, 1)[0];
    cartData.cartItems = cartItems;

    updateCartInSession(cartName, cartData);

    const req = {
        cartId: cartData.cartId,
        cartItemId: removedItem.cartItemId,
        customerId: localStorage.getItem("customerId"),
    };

    try {
        const response = await dispatch(removeCartItem(req)).unwrap();
        dispatch(fetchCartByCustomerId());
    } catch (error) {
        console.error("Failed to remove item", error);
    }
};

export const addItemToCart = async (cartItem: CartItem, dispatch: any): Promise<void> => {
    if (cartItem) {
        try {
            const response = await dispatch(addCartItem(cartItem)).unwrap();
        } catch (error) {
            console.error("Failed to add item", error);
        }
    }
};

export const addNewCustomerCart = async (cartItem: any, dispatch: any): Promise<void> => {
    if (cartItem) {
        try {
            const response = await dispatch(addNewCart(cartItem)).unwrap();
            dispatch(fetchCartByCustomerId());
        } catch (error) {
            console.error("Failed to add item", error);
        }
    }
};

export const updateItemToCart = async (cartItem: CartItem, dispatch: any): Promise<void> => {
    if (cartItem) {
        try {
            const response = await dispatch(updateCartItem(cartItem)).unwrap();
            dispatch(fetchCartByCustomerId());
        } catch (error) {
            console.error("Failed to add item", error);
        }
    }
};

export const getCartByCartName = (cartName: string): Cart | null => {
    const savedCarts = localStorage.getItem("customercarts");
    if (!savedCarts) return null;

    const customerCarts: Cart[] = JSON.parse(savedCarts);
    return customerCarts.find(cart => cart.cartName === cartName) || null;
};

export const updateCartInSession = (cartName: string, cartData: Cart): void => {
    const savedCarts = localStorage.getItem("customercarts");
    let customerCarts: Cart[] = savedCarts ? JSON.parse(savedCarts) : [];

    const cartIndex = customerCarts.findIndex(cart => cart.cartName === cartName);
    if (cartIndex !== -1) {
        customerCarts[cartIndex] = { ...customerCarts[cartIndex], ...cartData };
    } else {
        customerCarts.push(cartData);
    }

    localStorage.setItem("customercarts", JSON.stringify(customerCarts));
};

export const getCustomerCart = async (dispatch: any): Promise<Cart[] | null> => {
    const token = localStorage.getItem("token");
    const customerId = localStorage.getItem("customerId");

    if (!token || !customerId) {
        console.error("Missing authentication details");
        return null;
    }

    try {
        const response = await dispatch(fetchCartByCustomerId()).unwrap();
        return response;
    } catch (error) {
        console.error("Failed to fetch cart", error);
        return null;
    }
};
